import type { CSSProperties } from "react";
import { AppLink } from "@/shared/components/AppLink";
import type { PublishedArticleCard } from "@/features/content/types/content.types";
import { formatFirstPublishedDate } from "@/features/content/utils/contentDates";
import { useLocale, getLocalizedPath } from "@/shared/i18n/locale";
import { getHomeContent } from "@/content/home.content";
import styles from "./Articles.module.css";

interface ArticleShowcaseCardProps {
  article: PublishedArticleCard;
  variant: "featured" | "compact";
}

function formatPublicationDate(firstPublishedAt: string | null) {
  const formattedDate = formatFirstPublishedDate(firstPublishedAt);
  return formattedDate.startsWith("Data ") ? formattedDate : formattedDate.split(" ")[0];
}

export function ArticleShowcaseCard({
  article,
  variant,
}: ArticleShowcaseCardProps) {
  const locale = useLocale();
  const content = getHomeContent(locale);

  const imageStyle = {
    "--image-position": `${article.thumbnailPosition.x}% ${article.thumbnailPosition.y}%`,
    "--image-zoom": String((article.thumbnailPosition.zoom ?? 100) / 100),
  } as CSSProperties;

  const articleHref = getLocalizedPath(article.href, locale);
  const ariaLabel = locale === "en" ? `Read article: ${article.title}` : `Ler artigo: ${article.title}`;

  return (
    <article
      className={[
        styles.articleCard,
        variant === "featured" ? styles.featuredCard : styles.compactCard,
      ].join(" ")}
      data-variant={variant}
    >
      <AppLink
        className={styles.articleLink}
        href={articleHref}
        aria-label={ariaLabel}
      >
        {variant === "featured" && (
          <div className={styles.featuredMedia}>
            {article.thumbnail && (
              <img
                src={article.thumbnail}
                alt={article.thumbnailAlt || article.title}
                loading="lazy"
                decoding="async"
                style={imageStyle}
              />
            )}
          </div>
        )}

        <div className={styles.articleBody}>
          {variant === "featured" && (
            <div className={styles.articleMeta} aria-label={locale === "en" ? "Article information" : "Informações do artigo"}>
              <span>{article.area}</span>
              <span>{article.readingTime}</span>
            </div>
          )}
          <h3 className={styles.articleTitle}>{article.title}</h3>
          <time
            className={styles.articleDate}
            dateTime={article.firstPublishedAt ?? undefined}
          >
            {content.articles.publishedOn} {formatPublicationDate(article.firstPublishedAt)}
          </time>
        </div>
      </AppLink>
    </article>
  );
}
